import { replace } from 'connected-react-router'
import { takeLatest, call, fork, put, select } from 'redux-saga/effects'
import axios from 'axios'
import { getProductRoute } from '../routes'
import {
  LOGIN,
  LOGOUT,
  profile,
  profileSuccess,
  profileFailed,
  loginSuccess,
  loginFailed,
  logoutSuccess,
  logoutFailed
} from '../actions/auth-actions'

import {
  SET_PRODUCT_ONBOARDED,
  setProductOnboardedFailed,
  setProductOnboardedSuccess
} from '../actions/walkthrough-actions'

import authService from '../services/auth-service'
import ApiService from '../services/api-service'
import { getAuth } from './../selectors/session-selectors'
import { isSafeRedirectUrl } from '../utils/auth-helper'

import config from '../config'

export function getClientHeaders() {
  return {
    'xc-sc-size': window.screen ? `${window.screen.width} x ${window.screen.height}` : null,
    'xc-win-size': `${window.innerWidth} x ${window.innerHeight}`,
    'xc-tzo': new Date().getTimezoneOffset(),
    'xc-platform': navigator.platform || navigator.oscpu,
    'xc-href': window.location.href
  }
}

axios.interceptors.response.use(
  response => response,
  error => {
    if (error) {
      if (error.response && error.response.status && error.response.status === 401) {
        console.error('Intercepted Unauthorized API Access', error)
        authService.logout()
        authService.redirectToLogin()
      }
      return Promise.reject(error)
    } else {
      console.error('no axios interceptor error')
    }
  }
)

const fetchProfile = ({ token }) => ApiService.isAuth(token).get('/api/profile')

export function* refreshProfile() {
  const auth = yield select(getAuth)
  yield getProfile(auth)
}

function* getProfile(auth) {
  yield put(profile())
  try {
    const profile = auth && (yield fetchProfile(auth))

    yield put(profileSuccess(profile))

    if (profile.user !== undefined) {
      /* Set user context for error logs */
      window.heap && window.heap.identify(profile.user.id)
      if (config.log.sentry.enabled) {
        window.setUserContext({
          email: profile.user.email,
          id: profile.user.id,
          groups: profile.user.Groups ? profile.user.Groups.map(group => group.name).join(',') : []
        })
      }
    }
    return profile
  } catch (error) {
    yield put(profileFailed(error))
    console.error(error)
    if (error && error.response && error.response.status === 401) {
      authService.logout()
      return put(replace('/login'))
    } else {
      // capture non 401 errors
      window.captureException(error)
    }
  }
}

// Set by LoginPage before leaving for SSO, since the server does not carry redirectUrl through the round-trip
function takeSsoRedirectUrl() {
  try {
    const redirectUrl = window.sessionStorage.getItem('ssoRedirectUrl')
    window.sessionStorage.removeItem('ssoRedirectUrl')
    return redirectUrl
  } catch (error) {
    // sessionStorage unavailable: fall back to the query param / default route
    return null
  }
}

/**
 * @param {object} action.payload: { auth }
 */
function* login({ payload }) {
  try {
    const { auth } = payload

    authService.setAuth(auth)
    axios.defaults.headers.common.Authorization = `Bearer ${auth.token}`
    yield put(loginSuccess(auth))
    const profile = yield getProfile(auth)
    const { pathname, search } = window.location
    if (pathname === '/login' || pathname === '/sso-callback') {
      console.info('Profile has been loaded. Redirecting to user homepage...')
      let redirectUrl = new URLSearchParams(search).get('redirectUrl')
      if (pathname === '/sso-callback') {
        redirectUrl = takeSsoRedirectUrl() || redirectUrl
      }
      if (!isSafeRedirectUrl(redirectUrl)) {
        redirectUrl = yield getDefaultRoute(profile)
      }
      console.info('User homepage', redirectUrl)
      yield put(replace(redirectUrl))
    }
  } catch (error) {
    console.error('Error logging in user: ', error)
    window.captureException(error)
    if (error?.response?.status === 500) {
      yield put(loginFailed(error.response?.data?.name))
    } else {
      yield put(loginFailed(null))
    }
  }
}

function* getDefaultRoute(profile) {
  const state = yield select(state => state)
  return getProductRoute(profile.defaultProduct, state.session)
}

function* logout(action) {
  try {
    yield call([authService, authService.logout])
    yield put(logoutSuccess())
    window.setUserContext()
    if (!action.skipRedirect) yield put(replace('/login'))
  } catch (error) {
    window.captureException(error)
    yield put(logoutFailed(error))
  }
}

function* setProductOnboarded() {
  try {
    const { user } = yield select(state => state.session)
    const response = yield call(() =>
      axios.post(`${config.apiBaseUrl}/user/`, { id: user.id, onboardUserProduct: true })
    )

    if (response.status === 200) {
      yield put(setProductOnboardedSuccess())
    } else {
      yield put(setProductOnboardedFailed(response.data))
    }
  } catch (error) {
    window.captureException(error)
    yield put(setProductOnboardedFailed(error.message))
  }
}

// =====================================
//  WATCHERS
// -------------------------------------

function* watchLogin() {
  yield takeLatest(LOGIN, login)
}

function* watchLogout() {
  yield takeLatest(LOGOUT, logout)
}

function* watchProductOnboard() {
  yield takeLatest(SET_PRODUCT_ONBOARDED, setProductOnboarded)
}

// =====================================
//  AUTH SAGAS
// -------------------------------------

export default [fork(watchLogin), fork(watchLogout), fork(watchProductOnboard)]
